/**
 * Reads Google Ads ad-click attribution identifiers from the browser at signup
 * time — the Google counterpart to {@link getMetaClickIds}.
 *
 * When a user clicks a Google/PMax/YouTube ad with auto-tagging on, Google appends
 * `gclid` (Google Click Identifier) to the landing URL and the gtag/Google Ads tag
 * stores it in the `_gcl_aw` cookie (`GCL.<ts>.<gclid>`) on the registrable domain
 * (`.emergecareer.com`), readable across subdomains including the app. On iOS /
 * privacy-restricted clicks where `gclid` can't be set, Google instead appends
 * `wbraid` (web-to-web) or `gbraid` (app-to-web).
 *
 * We persist these on the user doc so the server-side Enhanced Conversions upload
 * (fired at signup and later milestones, potentially days later) can send the click
 * id for deterministic ad-click attribution — no browser needed at that point. This
 * is the direct analog of Meta's `fbc`: the strongest, click-based match, sent
 * alongside the hashed identifiers we already upload.
 *
 * `gclid`/`wbraid`/`gbraid` are Google's opaque click ids; they are sent RAW (never
 * hashed). Only keys that resolve to a value are included.
 */
export interface GoogleClickIds {
  gclid?: string;
  wbraid?: string;
  gbraid?: string;
}

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined" || !document.cookie) return undefined;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  if (!match) return undefined;
  const value = decodeURIComponent(match.slice(name.length + 1));
  return value || undefined;
}

/**
 * Parses the `gclid` out of the `_gcl_aw` cookie, whose value is
 * `GCL.<timestamp>.<gclid>`. The gclid is everything after the second segment
 * (defensive against a gclid that itself contains dots). Returns undefined when
 * the cookie is absent or malformed.
 */
function readGclidFromCookie(): string | undefined {
  const raw = readCookie("_gcl_aw");
  if (!raw) return undefined;
  const segments = raw.split(".");
  if (segments.length < 3) return undefined;
  const gclid = segments.slice(2).join(".");
  return gclid || undefined;
}

export function getGoogleClickIds(): GoogleClickIds {
  const params =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();

  // Prefer the fresh URL param; fall back to the `_gcl_aw` cookie so a gclid
  // captured on the marketing site survives navigation to the app subdomain.
  const gclid = params.get("gclid") || readGclidFromCookie();
  const wbraid = params.get("wbraid") || undefined;
  const gbraid = params.get("gbraid") || undefined;

  const ids: GoogleClickIds = {};
  if (gclid) ids.gclid = gclid;
  if (wbraid) ids.wbraid = wbraid;
  if (gbraid) ids.gbraid = gbraid;
  return ids;
}
